import moment from 'moment'
import { SupportedLanguage, normalizeLanguage } from './index'

// Map our i18next codes to moment's locale identifiers (matches MOMENT_LOCALES
// in index.ts; we keep a local copy to avoid a circular import on init).
export const MOMENT_LOCALES: Record<SupportedLanguage, string> = {
  ar: 'ar',
  en: 'en',
  es: 'es',
  fr: 'fr',
  pl: 'pl',
  tl: 'fil',
  vi: 'vi',
  zh: 'zh-cn',
}

/** Moment locale id for one of our supported languages (falls back to 'en'). */
export const momentLocaleFor = (lang?: string | null): string =>
  MOMENT_LOCALES[normalizeLanguage(lang)] ?? 'en'

/**
 * Format a date in the user's current language. Prefer moment's locale-aware
 * tokens (`LL`, `LLLL`, `LT`, etc.) over hardcoded month/day strings — they
 * pick the right ordering, ordinal, and month name per locale automatically.
 *
 * Pass `format` as either a moment locale-aware token (`LL`) or a literal
 * format string (`MMMM D, YYYY`). Locale-aware tokens are strongly preferred
 * because they auto-adapt: `LL` →
 *   en: "May 15, 2026"  ·  zh: "2026年5月15日"  ·  fr: "15 mai 2026"  ·
 *   ar: "١٥ مايو ٢٠٢٦"  ·  vi: "15 tháng 5 năm 2026"
 */
export const formatLocalizedDate = (
  date: string | number | Date | null | undefined,
  lang: string | null | undefined,
  format = 'LL'
): string => {
  if (!date) return ''
  const localeKey = momentLocaleFor(lang)
  return moment(date).locale(localeKey).format(format)
}

/**
 * Turns an English weekday name into its localized display form.
 *
 * Availability slots persist the English name ("Monday") as their value, so that
 * string is a contract with the API and must not change. Only what the advisor
 * reads is translated. `localeData().weekdays()` is indexed 0 = Sunday in every
 * locale regardless of where that locale starts its week, so the lookup is a
 * straight index map.
 */
export const localizedWeekday = (englishDay?: string | null, lang?: string | null): string => {
  if (!englishDay) return ''
  const index = moment.localeData('en').weekdays().indexOf(englishDay)
  if (index < 0) return englishDay
  return moment.localeData(momentLocaleFor(lang)).weekdays()[index]
}
